import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
} from '@dnd-kit/core'
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Textarea } from 'components/ui/textarea'
import { Select } from 'components/ui/select'
import { Checkbox } from 'components/ui/checkbox'
import {
	initializeTemplates,
	initializeTemplateTasks,
	createTemplate,
	updateTemplate,
	deleteTemplate,
	createTemplateTask,
	deleteTemplateTask,
} from 'slices/templateSlice'
import { GripVertical, Plus, Trash2, LayoutTemplate, Loader2 } from 'lucide-react'
import notify from 'components/ui/notify'

// ─── Constants ──────────────────────────────────────────────────────────────────

const PRIORITY_OPTIONS = [
	{ value: 'P1 - Urgent', label: 'P1 - Urgent' },
	{ value: 'P2 - High', label: 'P2 - High' },
	{ value: 'P3 - Medium', label: 'P3 - Medium' },
	{ value: 'P4 - Low', label: 'P4 - Low' },
]

const EFFORT_OPTIONS = [
	{ value: 'XS - Trivial', label: 'XS - Trivial' },
	{ value: 'S - Small', label: 'S - Small' },
	{ value: 'M - Medium', label: 'M - Medium' },
	{ value: 'L - Large', label: 'L - Large' },
	{ value: 'XL - Epic', label: 'XL - Epic' },
]

// ─── Types ──────────────────────────────────────────────────────────────────────

interface TaskData {
	_id: string
	taskName: string
	priority: string
	effort: string
	isNew?: boolean
}

// ─── SortableRow ────────────────────────────────────────────────────────────────

interface SortableRowProps {
	id: string
	seq: number
	task: TaskData
	onTaskNameChange: (id: string, value: string) => void
	onPriorityChange: (id: string, value: string) => void
	onEffortChange: (id: string, value: string) => void
	onDelete: (id: string) => void
	isSelected: boolean
	onSelect: (id: string, checked: boolean) => void
}

const SortableRow = ({
	id,
	seq,
	task,
	onTaskNameChange,
	onPriorityChange,
	onEffortChange,
	onDelete,
	isSelected,
	onSelect,
}: SortableRowProps) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		zIndex: isDragging ? 10 : undefined,
		opacity: isDragging ? 0.8 : 1,
	}

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={`grid grid-cols-[24px_32px_1fr_130px_120px_32px_32px] gap-1.5 py-1 px-1.5 items-center border-b border-gray-100 last:border-b-0 ${
				isDragging ? 'bg-blue-50 shadow-md rounded' : 'hover:bg-gray-50'
			}`}
		>
			<div
				{...attributes}
				{...listeners}
				className="flex items-center justify-center cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
			>
				<GripVertical className="w-3.5 h-3.5" />
			</div>

			<div className="text-xs text-gray-500 text-center font-medium">{seq}</div>

			<Input
				value={task.taskName}
				onChange={e => onTaskNameChange(id, e.target.value)}
				className="h-7 text-xs"
				placeholder="Task name"
			/>

			<Select value={task.priority} onChange={e => onPriorityChange(id, e.target.value)} className="h-7 text-xs">
				{PRIORITY_OPTIONS.map(opt => (
					<option key={opt.value} value={opt.value}>
						{opt.label}
					</option>
				))}
			</Select>

			<Select value={task.effort} onChange={e => onEffortChange(id, e.target.value)} className="h-7 text-xs">
				{EFFORT_OPTIONS.map(opt => (
					<option key={opt.value} value={opt.value}>
						{opt.label}
					</option>
				))}
			</Select>

			<div className="flex items-center justify-center">
				<Checkbox checked={isSelected} onCheckedChange={checked => onSelect(id, !!checked)} />
			</div>

			<button
				type="button"
				title="Delete task"
				onClick={() => onDelete(id)}
				className="flex items-center justify-center bg-transparent border-none cursor-pointer text-gray-400 hover:text-red-500 transition-colors p-0"
			>
				<Trash2 className="w-3.5 h-3.5" />
			</button>
		</div>
	)
}

// ─── Main Component ─────────────────────────────────────────────────────────────

const TasksTemplateCN = () => {
	const dispatch = useDispatch()
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const { userInfo } = useSelector((state: any) => state.userLogin)

	// Redux template state
	const { entities: templateEntities, ids: templateIds, isInitialized } = useSelector((state: any) => state.templates)
	const {
		entities: taskEntities,
		ids: taskIds,
		isLoading: tasksLoading,
	} = useSelector((state: any) => state.templates.tasks)

	// Filter to task-type templates
	const taskTemplates = useMemo(() => {
		const all = (templateIds || []).map((id: string) => templateEntities[id]).filter(Boolean)
		return all
			.filter((t: any) => t.templateType === 'task')
			.sort((a: any, b: any) => (a.templateName || '').localeCompare(b.templateName || ''))
	}, [templateIds, templateEntities])

	// ─── Editor State ───────────────────────────────────────────────────────────

	const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
	const [isCreateMode, setIsCreateMode] = useState(false)
	const [templateName, setTemplateName] = useState('')
	const [description, setDescription] = useState('')
	const [accessLevel, setAccessLevel] = useState('private')
	const [errors, setErrors] = useState<Record<string, string>>({})
	const [taskOrder, setTaskOrder] = useState<string[]>([])
	const [tasksMap, setTasksMap] = useState<Record<string, TaskData>>({})
	const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set())
	const [isDirty, setIsDirty] = useState(false)
	const [isSaving, setIsSaving] = useState(false)
	const [originalTaskIds, setOriginalTaskIds] = useState<Set<string>>(new Set())
	const nextTempId = useRef(0)

	// ─── Init ───────────────────────────────────────────────────────────────────

	useEffect(() => {
		;(dispatch as any)(initializeTemplates())
	}, [dispatch])

	// ─── Load template tasks when selection changes ─────────────────────────────

	useEffect(() => {
		if (selectedTemplateId) {
			;(dispatch as any)(initializeTemplateTasks(selectedTemplateId))
		}
	}, [dispatch, selectedTemplateId])

	// Populate editor when template tasks arrive
	useEffect(() => {
		if (!selectedTemplateId || isCreateMode) return
		const template = templateEntities[selectedTemplateId]
		if (!template) return

		setTemplateName(template.templateName || '')
		setDescription(template.description || '')
		setAccessLevel(template.accessLevel || 'private')

		const loaded = (taskIds || [])
			.map((id: string) => taskEntities[id])
			.filter(Boolean)
			.sort((a: any, b: any) => (a.seq || 0) - (b.seq || 0))

		const order: string[] = []
		const map: Record<string, TaskData> = {}
		const origIds = new Set<string>()
		loaded.forEach((t: any) => {
			order.push(t._id)
			map[t._id] = {
				_id: t._id,
				taskName: t.taskName,
				priority: t.priority || 'P3 - Medium',
				effort: t.effort || 'M - Medium',
			}
			origIds.add(t._id)
		})
		setTaskOrder(order)
		setTasksMap(map)
		setOriginalTaskIds(origIds)
		setSelectedTasks(new Set())
		setErrors({})
		setIsDirty(false)
	}, [taskIds, taskEntities, selectedTemplateId, isCreateMode])

	// ─── DnD ────────────────────────────────────────────────────────────────────

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
	)

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event
		if (over && active.id !== over.id) {
			setTaskOrder(prev => {
				const oldIndex = prev.indexOf(active.id as string)
				const newIndex = prev.indexOf(over.id as string)
				return arrayMove(prev, oldIndex, newIndex)
			})
			setIsDirty(true)
		}
	}

	// ─── Task Handlers ──────────────────────────────────────────────────────────

	const handleTaskNameChange = (id: string, value: string) => {
		setTasksMap(prev => ({ ...prev, [id]: { ...prev[id], taskName: value } }))
		setIsDirty(true)
	}

	const handlePriorityChange = (id: string, value: string) => {
		setTasksMap(prev => ({ ...prev, [id]: { ...prev[id], priority: value } }))
		setIsDirty(true)
	}

	const handleEffortChange = (id: string, value: string) => {
		setTasksMap(prev => ({ ...prev, [id]: { ...prev[id], effort: value } }))
		setIsDirty(true)
	}

	const handleTaskSelect = (id: string, checked: boolean) => {
		const newSet = new Set(selectedTasks)
		if (checked) newSet.add(id)
		else newSet.delete(id)
		setSelectedTasks(newSet)
	}

	const handleSelectAll = (checked: boolean) => {
		setSelectedTasks(checked ? new Set(taskOrder) : new Set())
	}

	const isAllSelected = taskOrder.length > 0 && taskOrder.every(id => selectedTasks.has(id))

	const handleDeleteTask = (id: string) => {
		const task = tasksMap[id]
		const name = task?.taskName?.trim() || 'this task'
		notify({
			title: 'Delete Task',
			text: `Are you sure you want to delete "${name}"?`,
			icon: 'warning',
			buttons: ['Cancel', 'Delete'],
			dangerMode: true,
		} as any).then((value: any) => {
			if (value) {
				setTaskOrder(prev => prev.filter(tid => tid !== id))
				setTasksMap(prev => {
					const next = { ...prev }
					delete next[id]
					return next
				})
				setSelectedTasks(prev => {
					const next = new Set(prev)
					next.delete(id)
					return next
				})
				setIsDirty(true)
			}
		})
	}

	const handleDeleteSelected = () => {
		if (selectedTasks.size === 0) return
		notify({
			title: 'Delete Selected Tasks',
			text: `Are you sure you want to delete ${selectedTasks.size} task${selectedTasks.size > 1 ? 's' : ''}?`,
			icon: 'warning',
			buttons: ['Cancel', 'Delete'],
			dangerMode: true,
		} as any).then((value: any) => {
			if (value) {
				setTaskOrder(prev => prev.filter(id => !selectedTasks.has(id)))
				setTasksMap(prev => {
					const next = { ...prev }
					selectedTasks.forEach(id => delete next[id])
					return next
				})
				setSelectedTasks(new Set())
				setIsDirty(true)
			}
		})
	}

	const handleAddTask = () => {
		const tempId = `_new_${nextTempId.current++}`
		const newTask: TaskData = {
			_id: tempId,
			taskName: '',
			priority: 'P3 - Medium',
			effort: 'M - Medium',
			isNew: true,
		}
		setTaskOrder(prev => [...prev, tempId])
		setTasksMap(prev => ({ ...prev, [tempId]: newTask }))
		setIsDirty(true)
	}

	// ─── Template List Handlers ─────────────────────────────────────────────────

	const switchTemplate = useCallback((templateId: string | null, createMode: boolean) => {
		setSelectedTemplateId(templateId)
		setIsCreateMode(createMode)
		if (createMode) {
			setTemplateName('')
			setDescription('')
			setAccessLevel('private')
			setTaskOrder([])
			setTasksMap({})
			setOriginalTaskIds(new Set())
			setSelectedTasks(new Set())
			setErrors({})
			setIsDirty(false)
		}
	}, [])

	const handleTemplateClick = useCallback(
		(templateId: string) => {
			if (templateId === selectedTemplateId) return
			const doSwitch = () => switchTemplate(templateId, false)
			if (isDirty) {
				;notify({
					text: 'You have unsaved changes. Discard them?',
					buttons: ['Cancel', 'Discard'],
					dangerMode: true,
				}).then((value: any) => {
					if (value) doSwitch()
				})
			} else {
				doSwitch()
			}
		},
		[selectedTemplateId, isDirty, switchTemplate]
	)

	const handleNewTemplate = useCallback(() => {
		const doSwitch = () => switchTemplate(null, true)
		if (isDirty) {
			;notify({
				text: 'You have unsaved changes. Discard them?',
				buttons: ['Cancel', 'Discard'],
				dangerMode: true,
			}).then((value: any) => {
				if (value) doSwitch()
			})
		} else {
			doSwitch()
		}
	}, [isDirty, switchTemplate])

	const handleDeleteTemplate = useCallback(
		(e: React.MouseEvent, templateId: string) => {
			e.stopPropagation()
			const template = templateEntities[templateId]
			;notify({
				text: `Delete template "${template?.templateName}"? This cannot be undone.`,
				buttons: ['Cancel', 'Delete'],
				dangerMode: true,
			}).then(async (value: any) => {
				if (value) {
					await (dispatch as any)(deleteTemplate(templateId))
					;(dispatch as any)(initializeTemplates())
					if (selectedTemplateId === templateId) {
						switchTemplate(null, false)
					}
				}
			})
		},
		[dispatch, templateEntities, selectedTemplateId, switchTemplate]
	)

	// ─── Save ───────────────────────────────────────────────────────────────────

	const validate = () => {
		const newErrors: Record<string, string> = {}
		if (!templateName.trim()) newErrors.templateName = 'Template name is required'
		if (!description.trim()) newErrors.description = 'Description is required'
		const emptyTask = taskOrder.find(id => !tasksMap[id]?.taskName.trim())
		if (emptyTask) newErrors.tasks = 'All tasks must have a name'
		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const handleSave = async () => {
		if (!validate()) return
		setIsSaving(true)

		try {
			if (isCreateMode) {
				// Create template
				const result = await (dispatch as any)(
					createTemplate({
						templateName: templateName.trim(),
						description: description.trim(),
						templateType: 'task',
						accessLevel,
					})
				)
				const newTemplateId = result.payload?._id
				if (!newTemplateId) throw new Error('Failed to create template')

				// Create tasks
				for (let i = 0; i < taskOrder.length; i++) {
					const t = tasksMap[taskOrder[i]]
					await (dispatch as any)(
						createTemplateTask({
							templateId: newTemplateId,
							taskName: t.taskName.trim(),
							seq: i + 1,
							priority: t.priority,
							effort: t.effort,
						})
					)
				}

				await (dispatch as any)(initializeTemplates())
				switchTemplate(newTemplateId, false)
				notify('Created!', `Template "${templateName.trim()}" has been created.`, 'success')
			} else if (selectedTemplateId) {
				// Update template metadata
				await (dispatch as any)(
					updateTemplate({
						_id: selectedTemplateId,
						templateName: templateName.trim(),
						description: description.trim(),
						accessLevel,
					})
				)

				// Delete all existing tasks then recreate in new order
				for (const origId of originalTaskIds) {
					await (dispatch as any)(deleteTemplateTask(origId))
				}
				for (let i = 0; i < taskOrder.length; i++) {
					const t = tasksMap[taskOrder[i]]
					await (dispatch as any)(
						createTemplateTask({
							templateId: selectedTemplateId,
							taskName: t.taskName.trim(),
							seq: i + 1,
							priority: t.priority,
							effort: t.effort,
						})
					)
				}

				await (dispatch as any)(initializeTemplates())
				await (dispatch as any)(initializeTemplateTasks(selectedTemplateId))
				notify('Saved!', `Template "${templateName.trim()}" has been updated.`, 'success')
			}
			setIsDirty(false)
		} catch (err: any) {
			notify('Error', err.message || 'Failed to save template', 'error')
		} finally {
			setIsSaving(false)
		}
	}

	// ─── Render ─────────────────────────────────────────────────────────────────

	const isEditorActive = isCreateMode || selectedTemplateId !== null
	const isSaveDisabled = !templateName.trim() || !description.trim() || isSaving

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />

			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				<div className="flex-1 flex overflow-hidden">
					<main className="flex-1 overflow-y-auto p-6">
						<h2 className="text-2xl font-semibold text-gray-800 mb-4">Task Templates</h2>

						<div className="max-w-[1080px] mx-auto w-full mt-4 flex flex-row gap-4 min-h-[600px]">
							{/* ── Left Panel: Template List ────────────────────────────── */}
							<div className="w-[260px] shrink-0 bg-white rounded-lg border border-gray-200 flex flex-col">
								<div className="px-3 py-2.5 border-b border-gray-200 flex items-center justify-between">
									<span className="text-sm font-semibold text-gray-700">Templates</span>
									<span className="text-xs text-gray-400">{taskTemplates.length}</span>
								</div>

								<div className="flex-1 overflow-y-auto">
									{!isInitialized ? (
										<div className="flex items-center justify-center py-10">
											<Loader2 className="w-5 h-5 animate-spin text-gray-400" />
										</div>
									) : taskTemplates.length === 0 ? (
										<div className="py-10 text-center text-sm text-gray-400">No templates yet</div>
									) : (
										taskTemplates.map((template: any) => (
											<div
												key={template._id}
												onClick={() => handleTemplateClick(template._id)}
												className={`group flex items-center justify-between px-3 py-2 cursor-pointer border-l-2 transition-colors ${
													selectedTemplateId === template._id
														? 'bg-[#0080fe]/5 text-[#0080fe] border-[#0080fe] font-semibold'
														: 'border-transparent hover:bg-gray-50 text-gray-700'
												}`}
											>
												<div className="min-w-0 flex-1">
													<div className="text-sm truncate">{template.templateName}</div>
													{template.description && (
														<div className="text-[11px] text-gray-400 truncate mt-0.5">{template.description}</div>
													)}
												</div>
												<button
													type="button"
													title="Delete template"
													onClick={e => handleDeleteTemplate(e, template._id)}
													className="opacity-0 group-hover:opacity-100 bg-transparent border-none cursor-pointer text-gray-400 hover:text-red-500 transition-all p-1 shrink-0"
												>
													<Trash2 className="w-3.5 h-3.5" />
												</button>
											</div>
										))
									)}
								</div>

								<div className="px-3 py-2 border-t border-gray-200">
									<Button variant="ghost" onClick={handleNewTemplate} className="w-full justify-start">
										<Plus className="w-4 h-4 mr-1.5" />
										New Template
									</Button>
								</div>
							</div>

							{/* ── Right Panel: Template Editor ─────────────────────────── */}
							<div className="flex-1 bg-white rounded-lg border border-gray-200 p-5 flex flex-col">
								{!isEditorActive ? (
									<div className="flex-1 flex flex-col items-center justify-center text-gray-400">
										<LayoutTemplate className="w-12 h-12 mb-3 text-gray-300" />
										<p className="text-sm">Select a template or create a new one</p>
									</div>
								) : (
									<>
										{/* Header Fields */}
										<div className="flex flex-col gap-2 mb-3">
											<div>
												<label className="block text-xs font-medium text-gray-700 mb-1">
													Template Name <span className="text-red-500">*</span>
												</label>
												<Input
													value={templateName}
													onChange={e => {
														setTemplateName(e.target.value)
														setIsDirty(true)
														if (errors.templateName) setErrors({ ...errors, templateName: '' })
													}}
													className={`text-xs h-8 ${errors.templateName ? 'border-red-500' : ''}`}
													placeholder="Template Name"
												/>
												{errors.templateName && (
													<p className="text-red-500 text-[0.7rem] mt-0.5">{errors.templateName}</p>
												)}
											</div>
											<div>
												<label className="block text-xs font-medium text-gray-700 mb-1">
													Description <span className="text-red-500">*</span>
												</label>
												<Textarea
													value={description}
													onChange={e => {
														setDescription(e.target.value)
														setIsDirty(true)
														if (errors.description) setErrors({ ...errors, description: '' })
													}}
													className={`text-xs min-h-[3.5rem] ${errors.description ? 'border-red-500' : ''}`}
													placeholder="Describe the purpose of this template"
													rows={2}
												/>
												{errors.description && (
													<p className="text-red-500 text-[0.7rem] mt-0.5">{errors.description}</p>
												)}
											</div>
										</div>

										{/* Access Level */}
										<div className="flex flex-row gap-4 mb-3 p-2 bg-gray-50 rounded text-xs">
											<span className="font-semibold text-gray-700 mr-2">Access:</span>
											{['private', 'org-wide', 'public'].map(level => (
												<label key={level} className="flex items-center gap-1.5 cursor-pointer">
													<input
														type="radio"
														name="accessLevel"
														value={level}
														checked={accessLevel === level}
														onChange={e => {
															setAccessLevel(e.target.value)
															setIsDirty(true)
														}}
														className="w-3.5 h-3.5 text-blue-600 cursor-pointer"
													/>
													{level === 'private' ? 'Private' : level === 'org-wide' ? 'Org-Wide' : 'Public'}
												</label>
											))}
										</div>

										{/* Task Table */}
										<div className="flex-1 overflow-y-auto border border-gray-200 rounded mb-3">
											{/* Header */}
											<div className="grid grid-cols-[24px_32px_1fr_130px_120px_32px_32px] gap-1.5 px-1.5 py-1.5 border-b border-gray-200 bg-gray-50 font-semibold text-xs sticky top-0 z-10">
												<span></span>
												<span className="text-center">Seq</span>
												<span>Task Name</span>
												<span>Priority</span>
												<span>Effort</span>
												<label className="flex items-center justify-center cursor-pointer">
													<Checkbox checked={isAllSelected} onCheckedChange={checked => handleSelectAll(!!checked)} />
												</label>
												<span></span>
											</div>

											{tasksLoading && !isCreateMode ? (
												<div className="flex items-center justify-center py-8">
													<Loader2 className="w-5 h-5 animate-spin text-gray-400" />
												</div>
											) : taskOrder.length === 0 ? (
												<div className="py-8 text-center text-sm text-gray-400">
													No tasks yet. Click "+ Add Task" to get started.
												</div>
											) : (
												<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
													<SortableContext items={taskOrder} strategy={verticalListSortingStrategy}>
														{taskOrder.map((id, index) => {
															const task = tasksMap[id]
															if (!task) return null
															return (
																<SortableRow
																	key={id}
																	id={id}
																	seq={index + 1}
																	task={task}
																	onTaskNameChange={handleTaskNameChange}
																	onPriorityChange={handlePriorityChange}
																	onEffortChange={handleEffortChange}
																	onDelete={handleDeleteTask}
																	isSelected={selectedTasks.has(id)}
																	onSelect={handleTaskSelect}
																/>
															)
														})}
													</SortableContext>
												</DndContext>
											)}
											{errors.tasks && <p className="text-red-500 text-[0.7rem] px-3 py-1">{errors.tasks}</p>}
										</div>

										{/* Actions */}
										<div className="flex items-center justify-between gap-2">
											<div className="flex items-center gap-2">
												<Button variant="ghost" onClick={handleAddTask}>
													<Plus className="w-4 h-4 mr-1" />
													Add Task
												</Button>
												{selectedTasks.size > 0 && (
													<Button variant="ghost-red" onClick={handleDeleteSelected}>
														<Trash2 className="w-4 h-4 mr-1" />
														Delete Selected ({selectedTasks.size})
													</Button>
												)}
											</div>
											<Button onClick={handleSave} disabled={isSaveDisabled}>
												{isSaving ? (
													<>
														<Loader2 className="w-4 h-4 mr-1 animate-spin" />
														Saving...
													</>
												) : (
													<>{isCreateMode ? 'Create Template' : 'Save Template'}</>
												)}
											</Button>
										</div>
									</>
								)}
							</div>
						</div>
					</main>
				</div>
			</div>
		</div>
	)
}

export default TasksTemplateCN
